import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { AuditTimeline } from '../../../shared/features/history/components/AuditTimeline';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { useHistory } from '../../../shared/features/history/hooks/use-history';
import { withinSection } from '../../../shared/components/section-trail';
import { ResetPasswordDrawer } from '../components/ResetPasswordDrawer';
import { UserActions } from '../components/UserActions';
import { UserDetailsPanel } from '../components/UserDetailsPanel';
import { useUserDetail } from '../hooks/use-user-detail';
import { USER_TAB_LABELS, USER_TABS, userTabPath, type UserTab } from '../users.paths';

function UserHistoryTab({ userId }: { userId: string }) {
  return <AuditTimeline state={useHistory('user', userId, true)} />;
}

/** One office user: their details and history, and the account actions (reset password, act as, deactivate) that would crowd every row of the list. */
export function UserDetailPage({ tab }: { tab: UserTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detail = useUserDetail(id);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const { state } = detail;

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading user" breadcrumbs={withinSection('users')} tabs={USER_TABS.length} />;
  if (state.status === 'not_found')
    return <EmptyState icon={FileX} title="User not found" description="This user doesn't exist, or the link is out of date." />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this user" description="Something went wrong loading this user. Try reloading the page." />;
  }

  const { user } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('users')}
        title={user.name}
        description={user.email}
        actions={
          <UserActions
            user={user}
            isYou={user.id === detail.currentUserId}
            hasTwoFactor={detail.hasTwoFactor}
            onResetPassword={() => {
              detail.resetResetPasswordError();
              setResetPasswordOpen(true);
            }}
            onActAs={detail.canImpersonate ? detail.actAs : undefined}
            onDeactivate={() => detail.deactivate(user.name)}
            onReactivate={() => detail.reactivate(user.name)}
          />
        }
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(userTabPath(user.id, key as UserTab))}
        items={[
          { key: 'details', label: USER_TAB_LABELS.details, children: <UserDetailsPanel user={user} /> },
          { key: 'history', label: USER_TAB_LABELS.history, children: <UserHistoryTab userId={user.id} /> },
        ]}
      />
      <ResetPasswordDrawer
        open={resetPasswordOpen}
        user={user}
        error={detail.resetPasswordError}
        submitting={detail.resettingPassword}
        onSubmit={(password) => detail.resetPassword(user, password)}
        onClose={() => setResetPasswordOpen(false)}
      />
    </>
  );
}
